import React from "react";
import LoggedOutPage from "./LoggedOutPage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useSearchParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Box, Typography } from "@mui/material";

// Helper function to find an enum value by its string representation
function getEnumByValue<T extends { [key: string]: string }>(
    enumObject: T,
    value: string | null,
): T[keyof T] | undefined {
    if (!value) return undefined;

    // Check if the value exists in the enum values
    const enumValues = Object.values(enumObject);
    return enumValues.includes(value as any) ? (value as T[keyof T]) : undefined;
}

export enum ErrorType {
    UNAUTHORIZED = "UNAUTHORIZED",
    NOT_FOUND = "NOT_FOUND",
    MISSING_CLIENT_CERTIFICATE = "MISSING_CLIENT_CERTIFICATE",
    CERTIFICATE_NOT_ACCEPTABLE = "CERTIFICATE_NOT_ACCEPTABLE",
}

export enum IconType {
    NONE = "NONE",
    WARNING = "WARNING",
}

const iconTypeMapping = {
    NONE: undefined,
    WARNING: (
        <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: 24, color: "#ff7700", marginRight: 16 }} />
    ),
    MISSING_CLIENT_CERTIFICATE: undefined,
    CERTIFICATE_NOT_ACCEPTABLE: undefined,
};

const messageMapping = {
    UNAUTHORIZED: "error_page.unauthorized_to_access_page",
    NOT_FOUND: "error_page.not_found",
    MISSING_CLIENT_CERTIFICATE: "error_page.client_cert",
    CERTIFICATE_NOT_ACCEPTABLE: "error_page.client_cert",
};

type ErrorPageProps = {
    type: ErrorType | "fromQueryParam";
    iconType?: IconType;
};

const ErrorPage = ({ type, iconType = IconType.WARNING }: ErrorPageProps) => {
    const [searchParams] = useSearchParams();

    if (type === "fromQueryParam") {
        const fromQueryParam = getEnumByValue(ErrorType, searchParams.get("type")?.toUpperCase() || null);
        if (fromQueryParam === undefined) console.error("Invalid error type in query param");
        type = fromQueryParam || ErrorType.NOT_FOUND;
    }

    return (
        <LoggedOutPage title={<FormattedMessage id={`error_page.title.${type.toLowerCase()}`} />}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100px",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        borderRadius: 1,
                        bgcolor: "background.paper",
                    }}
                >
                    {iconTypeMapping[iconType]}
                    <Typography variant="h6" component="div">
                        <FormattedMessage id={messageMapping[type]} />
                    </Typography>
                </Box>
            </Box>
        </LoggedOutPage>
    );
};

export default ErrorPage;
